import { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCcw, Fuel, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface ConsumptionData {
  id: string;
  code: string;
  expectedLiters: number | null;
  actualLiters: number | null;
  variancePct: number | null;
  status: 'normal' | 'above_expected' | 'high_waste' | 'investigate' | 'suspicious_low' | null;
  wasteCostSar: number;
  analyzedAt: string | null;
  kmAllocated: number | null;
  baselineLPer100km: number | null;
  baselineSource: 'route' | 'vehicle_overall' | 'none' | null;
  baselineSampleCount: number | null;
  priceSarPerL: number | null;
}

const STATUS_META: Record<string, { label: string; tone: string }> = {
  normal:           { label: 'Within expected',  tone: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30' },
  above_expected:   { label: 'Above expected',   tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  high_waste:       { label: 'High waste',       tone: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/30' },
  investigate:      { label: 'Investigate',      tone: 'bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/30' },
  suspicious_low:   { label: 'Suspiciously low', tone: 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/30' },
};

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtL   = (n: number | null) => n === null ? '—' : n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' L';
const fmtPct = (n: number | null) => n === null ? '—' : (n > 0 ? '+' : '') + n.toFixed(1) + '%';

export function TripConsumptionSection({ tripId }: { tripId: string }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  const [data, setData] = useState<ConsumptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reanalyzing, setReanalyzing] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<ConsumptionData>(`/api/v1/consumption/trips/${tripId}`);
      setData(r.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => { refresh(); }, [refresh]);

  async function reanalyze() {
    setReanalyzing(true);
    try {
      await apiClient.post(`/api/v1/consumption/trips/${tripId}/reanalyze`);
      await refresh();
    } catch (err) {
      window.alert('Reanalyze failed: ' + extractErrorMessage(err));
    } finally {
      setReanalyzing(false);
    }
  }

  if (loading && !data) {
    return (
      <div className="space-y-2 pt-2 border-t border-border">
        <div className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-2">
          <Fuel className="h-3.5 w-3.5" /> Consumption
        </div>
        <div className="rounded-lg border border-border bg-card p-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-2 pt-2 border-t border-border">
        <div className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-2">
          <Fuel className="h-3.5 w-3.5" /> Consumption
        </div>
        <div className="rounded-lg border border-border bg-card p-3 text-sm text-muted-foreground">
          {error ?? 'No data'}
        </div>
      </div>
    );
  }

  const meta = data.status ? STATUS_META[data.status] : null;
  const noBaseline = !data.expectedLiters || data.baselineSource === 'none';

  return (
    <div className="space-y-2 pt-2 border-t border-border">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-2">
          <Fuel className="h-3.5 w-3.5" /> Consumption
        </div>
        {isAdmin && (
          <Button variant="ghost" size="sm" onClick={reanalyze} disabled={reanalyzing} className="h-7 gap-1.5 text-xs">
            {reanalyzing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3" />}
            Reanalyze
          </Button>
        )}
      </div>

      <div className="rounded-lg border border-border bg-card p-3 space-y-2 text-sm">
        {noBaseline ? (
          <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400 text-xs">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <div>No baseline yet for this vehicle/route. Need a few completed trips before consumption can be scored.</div>
          </div>
        ) : null}

        <div className="grid grid-cols-3 gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Expected</div>
            <div className="font-mono">{fmtL(data.expectedLiters)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Actual</div>
            <div className="font-mono">{fmtL(data.actualLiters)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Variance</div>
            <div className="font-mono">{fmtPct(data.variancePct)}</div>
          </div>
        </div>

        {meta && (
          <div className="flex items-center justify-between gap-2">
            <span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ' + meta.tone}>
              {meta.label}
            </span>
            {data.wasteCostSar > 0 && (
              <span className="text-xs text-muted-foreground">
                Waste: <span className="font-mono text-foreground">{fmtSar(data.wasteCostSar)}</span>
              </span>
            )}
          </div>
        )}

        {data.baselineLPer100km !== null && data.kmAllocated !== null && (
          <div className="text-[10px] text-muted-foreground pt-1 border-t border-border">
            Baseline {data.baselineLPer100km.toFixed(2)} L/100km × {data.kmAllocated.toFixed(1)} km
            {data.baselineSource === 'route' ? ' (route)' : ' (vehicle overall)'}
            {data.priceSarPerL ? ` · diesel ${data.priceSarPerL.toFixed(2)} SAR/L` : ''}
          </div>
        )}
      </div>
    </div>
  );
}
